<template>
  <div id="dashboardWrap" ref="dashboardWrap" :class="[className, {on: isOpenFilter}]">
    <LazyUserCommonCreditBanner
      v-if="isOpenCreditBanner"
      @closeBanner="creditCookieOpen = false"/>

    <LazyUserCommonMenu />

    <LazyGlobalLoading />

    <!-- Filter is having elements -->
    <template v-if="havingFilter">
      <slot name="sidebar" />
    </template>

    <!-- Filter is none elements -->
    <template v-else>
      <main id="dashboardContent" :key="lang" :class="mainClassName">
        <slot name="content" />
      </main>
      <LazyUserCommonFooter
        :class="footerClassName"
        :is-dash-board="isDashboardMainFooterClass"
      />
      <LazyUserCommonReportGuideMoveButton v-if="isShowingReportGuideMoveButtonComponent" :tab="getReportRouteTab"/>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { useNuxtApp, useRouter, useRoute } from '#app';
import { useUserService } from '~/composables/service/useUserService';
import { removeCookie, getCookie } from '~~/package/utils/CookieUtil';
import { useLoadingStore } from '~/store/loading/loadingStore';
import { useUserStore } from '~/store/user/userStore';
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import {ReportTabType} from '~~/package/mapper/report/model/ReportTabType';

const { $i18n } = useNuxtApp();
const router = useRouter();
const route = useRoute();
const loadingStore = useLoadingStore();
const userStore = useUserStore();
const userService = useUserService();

const props = defineProps({
  isOpenFilter: { type: Boolean, default: false },
  havingFilter: { type: Boolean, default: false },
  mainClassName: { type: String, default: ""},
  footerClassName: { type: String, default: ""}
})

const className = ref("");
const isDashboardMainFooterClass = ref(false);
const creditCookieOpen = ref(getCookie(CookieKeyType.OPEN_CREDIT_BANNER));
const dashboardWrap = ref<HTMLElement | null>(null);

const lang = computed(() => $i18n.locale.value.toUpperCase());

// PAY-GATE-06, ASMT-CREDIT-09: 저잔액 배너 — Admin/SuperAdmin + 배너 쿠키 + credit <= 1.
const isOpenCreditBanner = computed(() => {
  return (userStore.isAdminType || userStore.isSuperAdminType) &&
    creditCookieOpen.value &&
    userStore.userV2.credit <= 1
})

const isShowingReportGuideMoveButtonComponent = computed(() => {
  const routeName = route.name;

  const isAssessment = routeName === 'dashboard-proSkillManager-assessment-assessmentId';
  const isResult = routeName === 'dashboard-proSkillManager-report';

  if (isAssessment || isResult) return true;

  return false;
});

const getReportRouteTab = computed(() => route.query.tab ?? 'ASSESSMENT')

const onClickLogOut = async () => {
  // SEC-1 (TSK-10568): clear server-side HttpOnly cookie + legacy JS cookie.
  await userService.userSignOut();
  removeCookie(CookieKeyType.ACCESS_TOKEN);
  removeCookie(CookieKeyType.OPEN_CREDIT_BANNER);
  userService.userLogOut();
  router.replace({ path: "/" });
};

const changeDashboardClassName = (route: string) => {
  const isDarkTheme = [
    "/dashboard/contest",
    "/dashboard/proSkillManager",
    "/dashboard/proSkillAssure"
  ].some(path => {
    if (path === "/dashboard/proSkillManager") {
      const excludePatterns = [
        /\/proSkillManager\/assessment\/\d+$/,   // 평가 상세
        /\/proSkillManager\/report$/             // 결과 페이지
      ];
      const isExcluded = excludePatterns.some(pattern => pattern.test(route));
      return route.includes(path) && !isExcluded;
    }
    return route.includes(path);
  });

  if (isDarkTheme) {
    className.value = "dark";
    loadingStore.openLoading("");
  } else if (route === "/dashboard") {
    isDashboardMainFooterClass.value = true;
    className.value = "";
  } else {
    className.value = "";
    loadingStore.openLoading("light");
  }
};

const updateUserData = async () => {
  await userService.setUser(false);
  userStore.updateUserV2(userService.getUserV2());
  userStore.isUserInitialized = false;
};

const isUserData = () => {
  if (userStore.isLogOut()) onClickLogOut();
};

let removeBeforeEachHook: (() => void) | null = null;

onMounted(() => {
  removeBeforeEachHook = router.beforeEach((to, from, next) => {
    if (from.fullPath && !from.fullPath.includes("dashboard")) {
      userStore.isUserInitialized = true;
    }
    next();
  });
  isUserData();
});

onUnmounted(() => {
  removeBeforeEachHook?.();
});

watch(
  () => route.path,
  () => {
    if (userStore.isUserInitialized) {
      updateUserData();
    }
    changeDashboardClassName(route.path);
  },
  { immediate: true }
);
</script>